import { useEffect, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Reveal from './Reveal'
import { useMetaPixel } from './useMetaPixel'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const showPrice = (precio) => {
  if (!precio) return false
  const n = parseFloat(precio)
  return !isNaN(n) && n > 0
}

// ─── Panel de info pixel (visible solo cuando admin lo activa) ────────────────

const PixelInfoPanel = ({ fromAd, pixelId, useGlobalPixel, pageViewActivo, visible }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.97 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: 'fixed', bottom: 72, left: 16, zIndex: 9998,
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: '14px 16px',
            background: 'rgba(10,9,6,0.96)',
            border: `1px solid ${fromAd ? 'rgba(109,191,143,0.45)' : 'rgba(201,168,76,0.25)'}`,
            backdropFilter: 'blur(8px)',
            fontFamily: "'Courier New', monospace", fontSize: 10,
            pointerEvents: 'none', minWidth: 260,
          }}
        >
          <div style={{ position: 'absolute', top: 0, left: 12, right: 12, height: 1, background: `linear-gradient(90deg, transparent, ${fromAd ? 'rgba(109,191,143,0.6)' : 'rgba(201,168,76,0.4)'}, transparent)` }} />

          <style>{`
            @keyframes pixel-pulse {
              0%, 100% { opacity: 0.6; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.3); }
            }
          `}</style>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: fromAd ? '#6DBF8F' : '#C9A84C', boxShadow: `0 0 6px ${fromAd ? 'rgba(109,191,143,0.8)' : 'rgba(201,168,76,0.6)'}`, animation: 'pixel-pulse 2s ease-in-out infinite' }} />
            <span style={{ color: fromAd ? '#6DBF8F' : '#C9A84C', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 9 }}>
              Meta Pixel — Admin Debug
            </span>
          </div>

          <div style={{ height: 1, background: 'rgba(201,168,76,0.1)' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>pixel_id</span>
              <span style={{ color: '#C9A84C', letterSpacing: '0.05em' }}>{pixelId || '–'}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>estado</span>
              <span style={{ color: pageViewActivo ? '#6DBF8F' : '#E07A7A', fontWeight: 700 }}>
                {pageViewActivo ? '1 — PageView activo' : '0 — PageView inactivo'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>pixel_global</span>
              <span style={{ color: useGlobalPixel ? '#6BA3E0' : '#C9A84C', fontWeight: 700 }}>
                {useGlobalPixel ? 'true' : 'false'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>modo</span>
              <span style={{ color: useGlobalPixel ? '#6BA3E0' : '#C9A84C' }}>
                {useGlobalPixel ? 'global' : 'individual'}
              </span>
            </div>

            <div style={{ height: 1, background: 'rgba(201,168,76,0.08)', margin: '2px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>tráfico</span>
              <span style={{ color: fromAd ? '#6DBF8F' : 'rgba(237,224,200,0.3)' }}>
                {fromAd ? '✓ desde anuncio' : '○ orgánico'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>pageview</span>
              <span style={{ color: pageViewActivo ? (fromAd ? '#6DBF8F' : 'rgba(201,168,76,0.8)') : '#E07A7A' }}>
                {pageViewActivo ? (fromAd ? 'disparado' : 'disparado (sin fbclid)') : 'no disparado'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'rgba(237,224,200,0.35)', letterSpacing: '0.08em' }}>purchase</span>
              <span style={{ color: fromAd ? '#6DBF8F' : 'rgba(224,122,122,0.7)' }}>
                {fromAd ? 'activo' : 'silenciado'}
              </span>
            </div>

          </div>

          <AnimatePresence>
            {!pageViewActivo && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
                <div style={{ marginTop: 6, padding: '7px 10px', background: 'rgba(224,122,122,0.08)', border: '1px solid rgba(224,122,122,0.25)', display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 2v6M6.3 6.3A8 8 0 1 0 12 4" stroke="#E07A7A" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span style={{ color: 'rgba(224,122,122,0.85)', fontSize: 9, lineHeight: 1.6, letterSpacing: '0.06em' }}>
                    PageView desactivado en BD. El pixel NO se cargó en el &lt;head&gt;.
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ─── Botón flotante admin ─────────────────────────────────────────────────────

const PixelAdminToggle = ({ visible, onToggle, fromAd, pageViewActivo }) => {
  const dotColor = !pageViewActivo ? '#E07A7A' : fromAd ? '#6DBF8F' : '#C9A84C'
  const strokeColor = !pageViewActivo ? '#E07A7A' : fromAd ? '#6DBF8F' : '#C9A84C'

  return (
    <motion.button
      onClick={onToggle}
      initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
      title={visible ? 'Ocultar info de pixel' : 'Ver info de pixel (admin)'}
      style={{
        position: 'fixed', bottom: 16, left: 16, zIndex: 9999,
        width: 44, height: 44, borderRadius: 0,
        border: `1px solid ${visible ? (!pageViewActivo ? 'rgba(224,122,122,0.5)' : fromAd ? 'rgba(109,191,143,0.6)' : 'rgba(201,168,76,0.6)') : 'rgba(201,168,76,0.2)'}`,
        background: visible ? (!pageViewActivo ? 'rgba(224,122,122,0.08)' : fromAd ? 'rgba(109,191,143,0.12)' : 'rgba(201,168,76,0.1)') : 'rgba(10,9,6,0.88)',
        backdropFilter: 'blur(8px)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'border-color 0.25s, background 0.25s', padding: 0,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        {visible ? (
          <>
            <path d="M2 9C2 9 4.5 5 9 5C13.5 5 16 9 16 9C16 9 13.5 13 9 13C4.5 13 2 9 2 9Z" stroke={strokeColor} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9" cy="9" r="2" stroke={strokeColor} strokeWidth="1.2" />
            <line x1="3" y1="3" x2="15" y2="15" stroke={strokeColor} strokeWidth="1.2" strokeLinecap="round" />
          </>
        ) : (
          <>
            <path d="M2 9C2 9 4.5 5 9 5C13.5 5 16 9 16 9C16 9 13.5 13 9 13C4.5 13 2 9 2 9Z" stroke="rgba(201,168,76,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9" cy="9" r="2" stroke="rgba(201,168,76,0.55)" strokeWidth="1.2" />
          </>
        )}
      </svg>
      <div style={{ position: 'absolute', top: 7, right: 7, width: 5, height: 5, borderRadius: '50%', background: dotColor, opacity: visible ? 1 : 0.5, transition: 'opacity 0.3s, background 0.3s' }} />
    </motion.button>
  )
}

// ─── Botón de compra ──────────────────────────────────────────────────────────

const BuyButton = ({ curso, onBeforeBuy }) => {
  const [fired, setFired] = useState(false)

  const handleClick = useCallback(() => {
    onBeforeBuy(curso)
    setFired(true)
    setTimeout(() => setFired(false), 1200)
  }, [curso, onBeforeBuy])

  return (
    <motion.a
      href={curso.link} target="_blank" rel="noopener noreferrer" onClick={handleClick}
      className="font-display font-black text-[10px] tracking-[0.1em] uppercase px-5 py-2.5 inline-flex items-center gap-2"
      style={{ background: fired ? '#6DBF8F' : 'var(--gold)', color: '#000', transition: 'background 0.25s', textDecoration: 'none', whiteSpace: 'nowrap' }}
      whileHover={{ backgroundColor: fired ? '#6DBF8F' : '#E2C47A' }} transition={{ duration: 0.15 }}
    >
      {fired ? (
        <><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>Registrado</>
      ) : 'Comprar'}
    </motion.a>
  )
}

// ─── Tarjeta de curso ─────────────────────────────────────────────────────────

const CursoCard = ({ curso, index, onBeforeBuy, useGlobalPixel, showPixelInfo, pageViewActivo }) => {
  const hasIndividualPixel = curso.pixel && curso.pixel.trim().length >= 10
  // estado_pixel del curso individual — controla si el Purchase se dispara
  const estadoPixelCurso   = Number(curso.estado_pixel) === 1

  return (
    <motion.div
    id="cursos"

      key={curso.id ?? curso.nombre_curso}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ backgroundColor: 'rgba(22,22,22,1)' }}
      className="relative flex flex-col overflow-hidden"
      style={{ background: 'var(--bg-2)' }}
    >
      {/* ── Badges admin — solo visibles cuando el panel está abierto ── */}
      <AnimatePresence>
        {showPixelInfo && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', pointerEvents: 'none' }}
          >
            {/* Badge pixel (global o individual) */}
            <div style={{ padding: '3px 8px', background: 'rgba(10,9,6,0.9)', border: `1px solid ${useGlobalPixel ? 'rgba(107,163,224,0.45)' : 'rgba(201,168,76,0.45)'}`, fontFamily: "'Courier New', monospace", fontSize: 9, fontWeight: 700, color: useGlobalPixel ? '#6BA3E0' : '#C9A84C', letterSpacing: '0.1em' }}>
              {useGlobalPixel ? 'PX: global' : hasIndividualPixel ? `PX: ${curso.pixel.slice(0, 8)}…` : 'PX: sin asignar'}
            </div>

            {/* Badge estado PageView */}
            <div style={{ padding: '3px 8px', background: 'rgba(10,9,6,0.9)', border: `1px solid ${pageViewActivo ? 'rgba(109,191,143,0.35)' : 'rgba(224,122,122,0.35)'}`, fontFamily: "'Courier New', monospace", fontSize: 9, fontWeight: 700, color: pageViewActivo ? '#6DBF8F' : '#E07A7A', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: pageViewActivo ? '#6DBF8F' : '#E07A7A', flexShrink: 0 }} />
              {pageViewActivo ? 'PV: activo' : 'PV: inactivo'}
            </div>

            {/* Badge estado_pixel del curso (Purchase) */}
            <div style={{ padding: '3px 8px', background: 'rgba(10,9,6,0.9)', border: `1px solid ${estadoPixelCurso ? 'rgba(109,191,143,0.35)' : 'rgba(224,122,122,0.35)'}`, fontFamily: "'Courier New', monospace", fontSize: 9, fontWeight: 700, color: estadoPixelCurso ? '#6DBF8F' : '#E07A7A', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
              {/* ícono carrito mini */}
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="9" cy="21" r="1" fill={estadoPixelCurso ? '#6DBF8F' : '#E07A7A'}/>
                <circle cx="20" cy="21" r="1" fill={estadoPixelCurso ? '#6DBF8F' : '#E07A7A'}/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke={estadoPixelCurso ? '#6DBF8F' : '#E07A7A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {estadoPixelCurso ? 'Purchase: on' : 'Purchase: off'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image header */}
      <div className="relative overflow-hidden" style={{ height: '320px', background: 'var(--bg-3)', borderBottom: '1px solid var(--line)' }}>
        {curso.imagen ? (
          <>
            <img src={curso.imagen} alt={curso.nombre_curso} className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover', objectPosition: 'center' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.04) 100%)' }} />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, transparent 60%)' }}>
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" opacity="0.35">
              <circle cx="26" cy="26" r="16" stroke="#C9A84C" strokeWidth="1.2" />
              <circle cx="26" cy="26" r="6"  stroke="#C9A84C" strokeWidth="1" />
              <line x1="26" y1="10" x2="26" y2="6"  stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="26" y1="46" x2="26" y2="42" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="10" y1="26" x2="6"  y2="26" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="46" y1="26" x2="42" y2="26" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-8">
        <h3 className="font-display font-black text-[18px] leading-[1.2] mb-2.5" style={{ color: 'var(--ink)' }}>
          {curso.nombre_curso}
        </h3>
        {curso.descripcion && (
          <p className="font-body font-light text-[13px] leading-[1.75] flex-1" style={{ color: 'var(--ink-2)' }}>
            {curso.descripcion}
          </p>
        )}
        <div className="flex items-center justify-between mt-6 pt-5" style={{ borderTop: '1px solid var(--line)' }}>
          {showPrice(curso.precio) ? (
            <span className="font-display font-black text-[20px]" style={{ color: 'var(--gold)' }}>${curso.precio}</span>
          ) : <div />}
          {curso.link && <BuyButton curso={curso} onBeforeBuy={onBeforeBuy} />}
        </div>
      </div>
    </motion.div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════

export default function Cursos() {
  const [cursos,  setCursos]  = useState([])
  const [loading, setLoading] = useState(true)

  // ── Auth admin desde localStorage ─────────────────────────────────────────
  const [isAdmin,        setIsAdmin]        = useState(false)
  const [showPixelPanel, setShowPixelPanel] = useState(false)

  useEffect(() => {
    if (localStorage.getItem('admin_auth') === 'true') setIsAdmin(true)
  }, [])

  // ── Hook del pixel ─────────────────────────────────────────────────────────
  const { globalPixelId, useGlobalPixel, fromAd, pageViewActivo, fireInitiateCheckout } = useMetaPixel()

  // ── Cargar cursos (con estado_pixel) ──────────────────────────────────────
  useEffect(() => {
    fetch('https://www.medicinacondante.com/cursos.php')
      .then((r) => r.json())
      .then((data) => {
        setCursos(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // ── Escuchar cambios de estado_pixel desde el admin ───────────────────────
  // Si el admin cambia el toggle de Purchase, recargamos los cursos
  // para reflejar el nuevo estado_pixel en cada tarjeta.
  // Usamos un polling liviano cada 10s solo si el panel está abierto.
  useEffect(() => {
    if (!isAdmin || !showPixelPanel) return
    const interval = setInterval(() => {
      fetch('https://www.medicinacondante.com/cursos.php')
        .then((r) => r.json())
        .then((data) => { if (Array.isArray(data)) setCursos(data) })
        .catch(() => {})
    }, 10000)
    return () => clearInterval(interval)
  }, [isAdmin, showPixelPanel])

  const handleTogglePixelPanel = useCallback(() => setShowPixelPanel((prev) => !prev), [])

  return (
    <section id="cursos" className="relative overflow-hidden" style={{ padding: '120px 5vw', background: 'var(--bg-2)' }}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.025]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #C9A84C 0, #C9A84C 1px, transparent 1px, transparent 80px), repeating-linear-gradient(90deg, #C9A84C 0, #C9A84C 1px, transparent 1px, transparent 80px)' }} />

      <div className="relative z-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="shrink-0">
              <div className="section-label">Cursos disponibles</div>
              <h2 className="font-display font-black leading-[1.04] tracking-[-0.02em]" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', color: 'var(--ink)' }}>
                Formación diseñada<br />para{' '}
                <em className="not-italic" style={{ color: 'var(--gold)' }}>resultados reales</em>.
              </h2>
            </div>
            <div className="flex flex-col gap-4 max-w-sm" style={{ borderLeft: '2px solid rgba(201,168,76,0.25)', paddingLeft: 20 }}>
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>Cada curso está pensado como un entrenamiento: primero ordenás el tema, después lo razonás y finalmente lo llevás a preguntas reales.</p>
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>Dejás de estudiar temas aislados y empezás a ver el sistema completo: qué importa, cómo se conecta y cómo te lo pueden tomar.</p>
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>Te doy un mapa claro de qué estudiar, cómo estudiarlo, cómo razonarlo y cómo responder en el examen.</p>
            </div>
          </div>
        </Reveal>

        {loading && (
          <div className="flex justify-center py-24">
            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(201,168,76,0.2)', borderTopColor: '#C9A84C', animation: 'cursos-spin 0.9s linear infinite' }} />
            <style>{`@keyframes cursos-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {!loading && cursos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '1px', background: 'var(--line)' }}>
            {cursos.map((c, i) => (
              <CursoCard
                key={c.id ?? c.nombre_curso}
                curso={c} index={i}
                onBeforeBuy={fireInitiateCheckout}
                useGlobalPixel={useGlobalPixel}
                showPixelInfo={isAdmin && showPixelPanel}
                pageViewActivo={pageViewActivo}
              />
            ))}
          </div>
        )}

        {!loading && cursos.length === 0 && (
          <div className="flex flex-col items-center py-24 gap-4">
            <p className="font-body font-light text-[13px]" style={{ color: 'var(--ink-3)' }}>No hay cursos disponibles por el momento.</p>
          </div>
        )}
      </div>

      {/* ── Controles de admin ── */}
      {isAdmin && (
        <>
          <PixelInfoPanel fromAd={fromAd} pixelId={globalPixelId} useGlobalPixel={useGlobalPixel} pageViewActivo={pageViewActivo} visible={showPixelPanel} />
          <PixelAdminToggle visible={showPixelPanel} onToggle={handleTogglePixelPanel} fromAd={fromAd} pageViewActivo={pageViewActivo} />
        </>
      )}
    </section>
  )
}